/**
 * Custom hook for managing Agent Node state and operations
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams } from 'react-router'
import { useReactFlow } from '@xyflow/react'
import { toast } from 'sonner'
import { useUser } from '@/context/UserContext'
import {
    useCreateAgent,
    useUpdateAgent,
    useGetAgentLogs,
    useGetAgentTasks,
} from '@/stores/agentStore'
import { AgentNodeData, AgentState, AgentDialogState } from '../types'

export function useAgentNodeState(id: string, data: AgentNodeData) {
    const { updateNodeData } = useReactFlow()
    const { projectId } = useParams()
    const user = useUser()
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Agent store hooks
    const createAgentMutation = useCreateAgent()
    const updateAgentMutation = useUpdateAgent()
    const fetchAgentLogsMutation = useGetAgentLogs()
    const fetchAgentTasksMutation = useGetAgentTasks()

    // Initialize state
    const [state, setState] = useState<AgentState>({
        isLocked: false,
        isMinimized: false,
        minimizeLevel: data.minimizeLevel || 'normal',
        isEditingTitle: false,
        editingTitleValue: data.name || 'Agent',
        manualTaskInstructions: '',
        selectedNodes: [],
        agentLogs: [],
        agentTasks: [],
        avatarGenerating: false,
        avatarUploading: false,
        dialogs: {
            showAgentSettings: false,
            showAgentLogs: false,
            showAgentUsage: false,
            showAgentCollaboration: false,
            showAgentQdrant: false,
            showAgentScheduling: false,
            showManualTask: false,
            isDataSettingsDialogVisible: false,
        },
    })

    // Auto-update label from name if needed
    useEffect(() => {
        if (
            (data.label === undefined ||
                data.label === null ||
                data.label === '' ||
                data.label === 'Agent') &&
            data.name &&
            data.name !== 'Agent'
        ) {
            updateNodeData(id, { ...data, label: data.name })
        }
    }, [data, id, updateNodeData])

    // Fetch agent logs and tasks when agentId changes
    useEffect(() => {
        if (data.agentId && projectId) {
            fetchAgentLogsMutation.execute({
                projectId,
                agentId: data.agentId,
                onSuccess: (result) => {
                    setState(prev => ({ ...prev, agentLogs: result?.data || [] }))
                },
                onError: (error) => {
                    console.error('Failed to fetch agent logs:', error)
                    setState(prev => ({ ...prev, agentLogs: [] }))
                },
            })

            fetchAgentTasksMutation.execute({
                projectId,
                agentId: data.agentId,
                onSuccess: (result) => {
                    setState(prev => ({ ...prev, agentTasks: result?.data || [] }))
                },
                onError: (error) => {
                    console.error('Failed to fetch agent tasks:', error)
                    setState(prev => ({ ...prev, agentTasks: [] }))
                },
            })
        }
    }, [data.agentId, fetchAgentLogsMutation, fetchAgentTasksMutation, projectId])

    // Get current agent (simplified)
    const agent = useMemo(() => {
        // TODO: Implement agent lookup when we have access to agents state
        return null
    }, [data.agentId])

    // Handlers
    const updateState = useCallback((updates: Partial<AgentState>) => {
        setState(prev => ({ ...prev, ...updates }))
    }, [])

    const updateDialogState = useCallback((updates: Partial<AgentDialogState>) => {
        setState(prev => ({
            ...prev,
            dialogs: { ...prev.dialogs, ...updates }
        }))
    }, [])

    const handleLock = useCallback(() => {
        updateState({ isLocked: !state.isLocked })
    }, [state.isLocked, updateState])

    const handleMinimize = useCallback(() => {
        updateState({ isMinimized: !state.isMinimized })
    }, [state.isMinimized, updateState])

    const handleMinimizeLevelChange = useCallback((level: 'normal' | 'medium' | 'minimum') => {
        updateState({ minimizeLevel: level })
        updateNodeData(id, { ...data, minimizeLevel: level })
    }, [id, data, updateNodeData, updateState])

    const handleDelete = useCallback(() => {
        // TODO: Implement delete logic
        console.log('Delete agent node:', id)
    }, [id])

    const handleDuplicate = useCallback(() => {
        // TODO: Implement duplicate logic  
        console.log('Duplicate agent node:', id)
    }, [id])

    const handleTitleSave = useCallback(() => {
        updateNodeData(id, { ...data, name: state.editingTitleValue, label: state.editingTitleValue })
        updateState({ isEditingTitle: false })
    }, [id, data, state.editingTitleValue, updateNodeData, updateState])

    const handleTitleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleTitleSave()
        } else if (e.key === 'Escape') {
            updateState({
                isEditingTitle: false,
                editingTitleValue: data.name || 'Agent'
            })
        }
    }, [handleTitleSave, data.name, updateState])

    // Agent-specific handlers
    const handleCreateAgent = useCallback(async () => {
        if (!projectId) {
            toast.error('No project selected')
            return
        }

        try {
            const agentData = {
                projectId,
                name: data.name || 'New Agent',
                role: data.role || 'general',
                type: 'assistant',
                enabled: data.enabled || false,
                instructions: '',
                triggerEvents: [],
                observationList: data.observationList || [],
                settings: {},
            }

            createAgentMutation.execute(agentData, {
                onSuccess: (result) => {
                    console.log('Agent created successfully:', result)
                    if (result?.data || result) {
                        const newAgent = result.data || result
                        updateNodeData(id, {
                            ...data,
                            agentId: newAgent.id,
                            name: newAgent.name,
                            role: newAgent.role,
                            enabled: newAgent.enabled,
                        })
                        toast.success('Agent created successfully')
                    }
                },
                onError: (error) => {
                    console.error('Failed to create agent:', error)
                    toast.error('Failed to create agent')
                },
            })
        } catch (error) {
            console.error('Failed to create agent:', error)
            toast.error('Failed to create agent')
        }
    }, [data, id, projectId, createAgentMutation, updateNodeData])

    const handleUpdateAgent = useCallback(async (updates: Partial<AgentNodeData>) => {
        if (!data.agentId || !projectId) return

        try {
            const updateData = {
                projectId,
                agentId: data.agentId,
                ...updates,
            }

            updateAgentMutation.execute(updateData, {
                onSuccess: (result) => {
                    console.log('Agent updated successfully:', result)
                    if (result?.data || result) {
                        updateNodeData(id, { ...data, ...updates })
                        toast.success('Agent updated successfully')
                    }
                },
                onError: (error) => {
                    console.error('Failed to update agent:', error)
                    toast.error('Failed to update agent')
                },
            })
        } catch (error) {
            console.error('Failed to update agent:', error)
            toast.error('Failed to update agent')
        }
    }, [data, id, projectId, updateAgentMutation, updateNodeData])

    const handleGenerateAvatar = useCallback(async () => {
        if (!projectId) {
            toast.error('Project not found')
            return
        }

        updateState({ avatarGenerating: true })
        // TODO: Implement avatar generation logic
        setTimeout(() => {
            updateState({ avatarGenerating: false })
            toast.success('Avatar generation would happen here')
        }, 2000)
    }, [projectId, updateState])

    const handleAvatarUpload = useCallback(async (file: File) => {
        if (!projectId || !user) {
            toast.error('Project or user not found')
            return
        }

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file')
            return
        }

        updateState({ avatarUploading: true })
        // TODO: Implement avatar upload logic
        setTimeout(() => {
            updateState({ avatarUploading: false })
            toast.success('Avatar upload would happen here')
        }, 2000)
    }, [projectId, user, updateState])
    // Compute node status
    const nodeStatus = useMemo((): 'initial' | 'loading' | 'error' | 'success' => {
        if (data.agentCommand?.processing) return 'loading'
        if (data.agentCommand?.error) return 'error'
        if (data.enabled) return 'success'
        return 'initial'
    }, [data.agentCommand?.processing, data.agentCommand?.error, data.enabled])

    return {
        // State
        ...state,

        // Computed values
        agent,
        logs: state.agentLogs,
        tasks: state.agentTasks,
        nodeStatus,
        fileInputRef,

        // State updaters
        updateState,
        updateDialogState,

        // Handlers
        handleLock,
        handleMinimize,
        handleMinimizeLevelChange,
        handleDelete,
        handleDuplicate,
        handleTitleSave,
        handleTitleKeyPress,
        handleCreateAgent,
        handleUpdateAgent,
        handleGenerateAvatar,
        handleAvatarUpload,
    }
}
